import Select, { type FormatOptionLabelMeta } from '@databases/atlaskit/Select';
import { messages } from '@databases/intl/messages';
import { DatabaseFieldType, type RawDatabaseValue } from '@databases/types/database';
import {
  type HyperlinkChange,
  MultiValueChangeType
} from '@databases/utils/database/changesets';
import { generateId } from '@databases/utils/utils';
import React, { type FunctionComponent, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl-next';

import { compactOptionSelectStyleConfig } from '../popover-edit-common';
import { type EditComponentProps } from '../types';

type Value = RawDatabaseValue<DatabaseFieldType.HYPERLINK>[0];
type HyperlinkSelectOption = Value & { isCreateOption?: boolean };

const getOptionValue = (option: HyperlinkSelectOption) => option.url;
const getOptionLabel = (option: HyperlinkSelectOption) => option.url;

type HyperlinkDefaultValueSelectProps = Pick<
  EditComponentProps<DatabaseFieldType.HYPERLINK>,
  | 'ariaLabel'
  | 'autoFocus'
  | 'field'
  | 'isInvalid'
  | 'onBlur'
  | 'onChange'
  | 'onFocus'
  | 'value'
  | 'aria-describedby'
>;

export const HyperlinkDefaultValueSelect: FunctionComponent<
  HyperlinkDefaultValueSelectProps
> = ({
  ariaLabel,
  field,
  isInvalid,
  onBlur,
  onChange,
  onFocus,
  value,
  'aria-describedby': ariaDescribedBy
}) => {
  const [hyperlinkSelectInputValue, setHyperlinkSelectInputValue] = useState('');
  const [createdHyperlinkSelectOptions, setCreatedHyperlinkSelectOptions] = useState<
    HyperlinkSelectOption[]
  >([]);
  const { formatMessage } = useIntl();
  const isMulti = field.settings.multi || value.length > 1;
  const addLinkMessage = formatMessage(
    messages['hyperlink-value-popover-edit.add-hyperlink-value']
  );

  const preserveCreatedHyperlinkSelectOptions = useCallback(
    (links: HyperlinkSelectOption[]) => {
      setCreatedHyperlinkSelectOptions((currentOptions) => {
        const optionsByUrl = new Map<string, HyperlinkSelectOption>();
        currentOptions.forEach((option) => {
          if (option.url.trim().length > 0) {
            optionsByUrl.set(option.url, option);
          }
        });
        links.forEach((link) => {
          if (link.url.trim().length > 0) {
            optionsByUrl.set(link.url, { _id: link._id, url: link.url });
          }
        });
        return Array.from(optionsByUrl.values());
      });
    },
    []
  );

  const addHyperlinkValueToCollab = React.useCallback(
    (val: Value, allValues: Value[]) => {
      if (val.url === '') {
        return;
      }

      const hyperlinkValues = isMulti ? [...allValues, val] : [val];

      const delta: HyperlinkChange = isMulti
        ? {
            type: MultiValueChangeType.APPEND,
            value: val
          }
        : {
            type: MultiValueChangeType.SET_TO,
            value: [val]
          };

      onChange(hyperlinkValues, {
        type: DatabaseFieldType.HYPERLINK,
        delta
      });
    },
    [onChange, isMulti]
  );

  const hyperlinkSelectValue = useMemo(
    () => value.filter((link) => link.url.trim().length > 0),
    [value]
  );

  const hyperlinkSelectOptions = useMemo((): HyperlinkSelectOption[] => {
    const trimmedInputValue = hyperlinkSelectInputValue.trim();
    const existingOptions = [...createdHyperlinkSelectOptions, ...hyperlinkSelectValue];
    const hasExistingOptionForInput = existingOptions.some(
      (option) => option.url === trimmedInputValue
    );
    const optionsByUrl = new Map<string, HyperlinkSelectOption>();

    if (trimmedInputValue && !hasExistingOptionForInput) {
      optionsByUrl.set(trimmedInputValue, {
        _id: `create-${trimmedInputValue}`,
        url: trimmedInputValue,
        isCreateOption: true
      });
    }

    createdHyperlinkSelectOptions.forEach((option) => {
      if (option.url.trim().length > 0) {
        optionsByUrl.set(option.url, option);
      }
    });
    hyperlinkSelectValue.forEach((link) => {
      optionsByUrl.set(link.url, link);
    });

    return Array.from(optionsByUrl.values());
  }, [createdHyperlinkSelectOptions, hyperlinkSelectInputValue, hyperlinkSelectValue]);

  const handleHyperlinkSelectInputChange = useCallback(
    (inputValue: string, meta: { action: string }) => {
      if (meta.action === 'input-change') {
        setHyperlinkSelectInputValue(inputValue);
      }
    },
    []
  );

  const handleHyperlinkSelectKeyDown = useCallback((event: React.KeyboardEvent) => {
    if (event.key === 'Backspace' || event.key === 'Delete') {
      event.stopPropagation();
      event.nativeEvent.stopImmediatePropagation?.();
    }
  }, []);

  const handleHyperlinkSelectChange = useCallback(
    (selectedValue: HyperlinkSelectOption | readonly HyperlinkSelectOption[] | null) => {
      if (Array.isArray(selectedValue)) {
        const hyperlinkValues = selectedValue.map(({ _id, isCreateOption, url }) => ({
          _id: isCreateOption ? generateId() : _id,
          url
        }));
        preserveCreatedHyperlinkSelectOptions(hyperlinkValues);
        onChange(hyperlinkValues, {
          type: DatabaseFieldType.HYPERLINK,
          delta: {
            type: MultiValueChangeType.SET_TO,
            value: hyperlinkValues
          }
        });
        setHyperlinkSelectInputValue('');
        return;
      }

      const selectedOption = selectedValue as HyperlinkSelectOption | null;
      if (!selectedOption) {
        onChange([], {
          type: DatabaseFieldType.HYPERLINK,
          delta: {
            type: MultiValueChangeType.SET_TO,
            value: []
          }
        });
        return;
      }

      const newLink = { _id: generateId(), url: selectedOption.url };
      preserveCreatedHyperlinkSelectOptions([newLink]);
      addHyperlinkValueToCollab(newLink, value);
      setHyperlinkSelectInputValue('');
    },
    [addHyperlinkValueToCollab, onChange, preserveCreatedHyperlinkSelectOptions, value]
  );

  const formatHyperlinkOptionLabel = useCallback(
    (
      option: HyperlinkSelectOption,
      meta: FormatOptionLabelMeta<HyperlinkSelectOption>
    ) => {
      if (meta.context === 'menu' && option.isCreateOption) {
        return (
          <>
            {formatMessage(messages['field-settings.select-picker-create-text'])}
            {
              // Intentionally adding whitespace
              // eslint-disable-next-line formatjs/no-literal-string-in-jsx
              ' '
            }
            {option.url}
          </>
        );
      }
      return option.url;
    },
    [formatMessage]
  );

  return (
    <Select<HyperlinkSelectOption, boolean>
      aria-describedby={ariaDescribedBy}
      aria-label={ariaLabel}
      blurInputOnSelect={false}
      closeMenuOnSelect={false}
      controlShouldRenderValue
      formatOptionLabel={formatHyperlinkOptionLabel}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
      hideSelectedOptions={false}
      inputValue={hyperlinkSelectInputValue}
      isClearable={hyperlinkSelectValue.length > 0}
      isInvalid={isInvalid}
      isMulti={isMulti}
      noOptionsMessage={() => formatMessage(messages['generic-select.no-options'])}
      onBlur={onBlur}
      onChange={handleHyperlinkSelectChange}
      onFocus={onFocus}
      onInputChange={handleHyperlinkSelectInputChange}
      onKeyDown={handleHyperlinkSelectKeyDown}
      options={hyperlinkSelectOptions}
      placeholder={addLinkMessage}
      preserveInputValue
      spacing="compact"
      styles={compactOptionSelectStyleConfig}
      value={hyperlinkSelectValue}
    />
  );
};
